import { Family, Tier, WILD_SPAWN_CHANCE } from '../config';
import { FamilyProgressionManager } from './FamilyProgressionManager';
import { elementIdFor, getElementDef, ElementInstance } from '../data/elements';

/** Base chance to bias spawn toward a singleton on the board (vs fully random). */
export const SMART_SPAWN_BIAS_BASE = 0.7;
/** Minimum smart spawn bias at high scores. */
export const SMART_SPAWN_BIAS_MIN = 0.2;
/** Score at which smart spawn bias reaches minimum. */
export const SMART_SPAWN_BIAS_SCALE_SCORE = 5000;

/** Default size of the lookahead queue for NEXT preview. */
export const DEFAULT_SPAWN_QUEUE_SIZE = 3;

/**
 * Picks what element to spawn. Pure selection logic — no grid side effects.
 *
 * Maintains a small lookahead queue so the HUD can render a "NEXT 3" preview.
 * The queue is pre-rolled with the same wild/family distribution the direct
 * pipeline uses — smart-bias is still evaluated at consumption time against
 * the current board.
 */
export class SpawnSystem {
  private familyProgression: FamilyProgressionManager;
  private queue: string[] = [];
  private readonly queueSize: number;

  constructor(familyProgression: FamilyProgressionManager, queueSize: number = DEFAULT_SPAWN_QUEUE_SIZE) {
    this.familyProgression = familyProgression;
    this.queueSize = queueSize;
  }

  /**
   * Pick a defId for a new spawn.
   * 5% chance of wild fragment, otherwise random active-family fragment.
   * Drains the head of the lookahead queue; the queue refills from the tail.
   */
  pickSpawnDefId(): string {
    this.fillQueue(1);
    const head = this.queue.shift() ?? this.generateOne();
    this.fillQueue(this.queueSize);
    return head;
  }

  /**
   * Calculate the smart spawn bias based on current score.
   * Starts at SMART_SPAWN_BIAS_BASE, linearly decreases to SMART_SPAWN_BIAS_MIN.
   */
  getSmartBias(score: number): number {
    const t = Math.min(score / SMART_SPAWN_BIAS_SCALE_SCORE, 1);
    return SMART_SPAWN_BIAS_BASE - t * (SMART_SPAWN_BIAS_BASE - SMART_SPAWN_BIAS_MIN);
  }

  /**
   * Smart spawn: biased toward creating a merge opportunity.
   * Bias decreases as score rises — early game is forgiving, late game is chaotic.
   * Always consumes one queue entry so the NEXT preview advances in lockstep
   * with actual spawns. If the smart-bias hit picks a singleton, the consumed
   * head is replaced by the singleton; otherwise the head is returned as-is.
   */
  pickSmartSpawnDefId(boardElements: ElementInstance[], score: number = 0): string {
    this.fillQueue(1);
    const head = this.queue.shift() ?? this.generateOne();
    this.fillQueue(this.queueSize);

    // Wild already rolled at queue-fill time; preserve it.
    if (head === elementIdFor(Family.Wild, Tier.Fragment)) {
      return head;
    }

    const bias = this.getSmartBias(score);
    if (Math.random() < bias) {
      const singleton = this.findSingletonDefId(boardElements);
      if (singleton) return singleton;
    }

    return head;
  }

  /**
   * Peek the next `n` spawn defIds without consuming them. Materialises
   * queue entries on demand so the HUD can render the NEXT-N preview.
   */
  peekNext(n: number = DEFAULT_SPAWN_QUEUE_SIZE): string[] {
    this.fillQueue(n);
    return this.queue.slice(0, n);
  }

  /**
   * Discard any pre-rolled picks. Call whenever the spawnable family pool
   * changes (draft, modifier, unlock) so stale previews don't lie.
   */
  invalidateQueue(): void {
    this.queue = [];
  }

  private fillQueue(min: number): void {
    const target = Math.max(min, this.queueSize);
    while (this.queue.length < target) {
      this.queue.push(this.generateOne());
    }
  }

  private generateOne(): string {
    if (Math.random() < WILD_SPAWN_CHANCE) {
      return elementIdFor(Family.Wild, Tier.Fragment);
    }
    const family = this.pickSpawnFamily();
    return elementIdFor(family, Tier.Fragment);
  }

  /**
   * Find a defId that appears exactly once on the board (a singleton)
   * and is below Eternal tier. Returns a random singleton's defId,
   * or null if none exist.
   */
  findSingletonDefId(boardElements: ElementInstance[]): string | null {
    const counts = new Map<string, number>();
    for (const el of boardElements) {
      const def = getElementDef(el.defId);
      if (!def || def.tier >= Tier.Eternal || def.family === Family.Wild) continue;
      counts.set(el.defId, (counts.get(el.defId) ?? 0) + 1);
    }

    const singletons: string[] = [];
    for (const [defId, count] of counts) {
      if (count === 1) singletons.push(defId);
    }

    if (singletons.length === 0) return null;
    return singletons[Math.floor(Math.random() * singletons.length)];
  }

  /**
   * Pick a random family for spawning. Uses the run's *active* families
   * (the player's drafted pool, capped per MAX_ACTIVE_FAMILIES_PER_RUN),
   * not the full set of unlocked families. Falls back to unlocked if the
   * draft is empty (defensive — shouldn't happen in normal flow).
   */
  pickSpawnFamily(): Family {
    let families = this.familyProgression.getActiveFamilies();
    if (families.length === 0) {
      families = this.familyProgression.getUnlockedFamilies();
    }
    return families[Math.floor(Math.random() * families.length)];
  }

  /**
   * Pick a fragment from a different family than the given one.
   * Used for splinter cross-family bonus (Phase 3). Drawn from the active
   * draft, not the full unlocked pool, to keep runs coherent.
   */
  pickCrossFamilyFragment(excludeFamily: Family): string {
    let pool = this.familyProgression.getActiveFamilies()
      .filter(f => f !== excludeFamily && f !== Family.Wild);
    if (pool.length === 0) {
      // Defensive fallback: try the broader unlocked pool.
      pool = this.familyProgression.getUnlockedFamilies()
        .filter(f => f !== excludeFamily && f !== Family.Wild);
    }
    if (pool.length === 0) {
      return this.pickSpawnDefId();
    }
    const family = pool[Math.floor(Math.random() * pool.length)];
    return elementIdFor(family, Tier.Fragment);
  }
}
